import type {TreeOption} from 'naive-ui';
import {ViewMode} from '@puppycad/cad-core';
import {
    type BuildFeatureTreeProjectionOptions,
    type FeatureTreeNodeMeta,
    FeatureTreeNodeKind,
} from './feature-tree-types';

/**
 * 构建装配体正式基准对象的设计树投影。
 *
 * 设计说明：
 * 1. 调用方必须先按 `ReferenceCreationContext.Assembly` 筛选输入，避免零件态基准对象污染装配体根节点；
 * 2. 装配体 `replaceTree` 不经过零件 FeatureManager 时间线，因此这里独立生成正式基准面、轴、点节点；
 * 3. 三类对象共用 `createdAt` 排序，保证显示顺序与实际创建顺序一致；
 * 4. 本函数只生成纯树数据与动作元数据，不读取 Viewer，也不修改任何 CAD 主状态。
 *
 * @param input 已按装配体创建来源筛选的基准对象摘要。
 * @returns 装配体基准对象树节点及节点元数据。
 */
export function buildAssemblyDatumReferenceTreeProjection(
    input: Pick<BuildFeatureTreeProjectionOptions, 'datumPlanes' | 'datumAxes' | 'datumPoints'>,
) {
    // 节点数据与动作元数据必须在同一投影边界生成，避免树能显示但无法选中或编辑。
    const featureTreeNodeMap = new Map<string, FeatureTreeNodeMeta>();
    const treeNodes: TreeOption[] = [];

    // 基准面使用正式业务 ID 建立节点，禁止退回 Three.js Object3D ID。
    input.datumPlanes.forEach((plane) => {
        const key = `datum-plane-${plane.id}`;
        const node: TreeOption = {
            key,
            label: plane.name,
            type: ViewMode.DATUM_PLANE,
            iconType: 'plane',
            isLeaf: true,
            refObjectId: String(plane.id),
            statusHidden: !plane.isVisible(),
            createdAt: plane.createdAt,
        };
        treeNodes.push(node);
        featureTreeNodeMap.set(key, {
            kind: FeatureTreeNodeKind.DatumPlane,
            planeId: plane.id,
            canEditReferencePlane: plane.canEditReferencePlane === true,
        });
    });

    // 基准轴沿用与零件 FeatureManager 相同的 key 和元数据契约。
    input.datumAxes.forEach((axis) => {
        const key = `datum-axis-${axis.id}`;
        const node: TreeOption = {
            key,
            label: axis.name,
            type: 'datum-axis',
            iconType: 'line',
            isLeaf: true,
            refObjectId: String(axis.id),
            statusHidden: !axis.isVisible(),
            createdAt: axis.createdAt,
        };
        treeNodes.push(node);
        featureTreeNodeMap.set(key, {
            kind: FeatureTreeNodeKind.DatumAxis,
            axisId: axis.id,
            canEditReferenceAxis: axis.canEditReferenceAxis === true,
        });
    });

    // 装配体投影只接受用户正式基准点，默认原点继续由 GLB 基准槽位表达。
    input.datumPoints
        .filter((point) => !point.isOrigin)
        .forEach((point) => {
            const key = `datum-point-${point.id}`;
            const node: TreeOption = {
                key,
                label: point.name,
                type: 'datum-point',
                iconType: 'point',
                isLeaf: true,
                refObjectId: String(point.id),
                statusHidden: !point.isVisible(),
                createdAt: point.createdAt,
            };
            treeNodes.push(node);
            featureTreeNodeMap.set(key, {
                kind: FeatureTreeNodeKind.DatumPoint,
                pointId: point.id,
                canEditReferencePoint: point.canEditReferencePoint === true,
            });
        });

    // 三类参考几何共用创建时间排序，确保跨类型创建顺序不会被分组打乱。
    treeNodes.sort((left, right) => {
        const leftCreatedAt = Number(left.createdAt);
        const rightCreatedAt = Number(right.createdAt);
        const normalizedLeftCreatedAt = Number.isFinite(leftCreatedAt)
            ? leftCreatedAt
            : Number.MAX_SAFE_INTEGER;
        const normalizedRightCreatedAt = Number.isFinite(rightCreatedAt)
            ? rightCreatedAt
            : Number.MAX_SAFE_INTEGER;
        return normalizedLeftCreatedAt - normalizedRightCreatedAt;
    });

    return {
        treeNodes,
        featureTreeNodeMap,
    };
}
